import { useState, useEffect, useCallback } from 'react';
import { supabase, NearbyCheckIn } from '../lib/supabase';
import { analytics } from '../lib/analytics';

interface UseNearbyCheckinsProps {
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  enabled?: boolean;
}

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useNearbyCheckins({
  latitude,
  longitude,
  radiusKm = 5, // Start with smaller radius for faster loading
  enabled = true
}: UseNearbyCheckinsProps) {
  const [checkIns, setCheckIns] = useState<NearbyCheckIn[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce location changes to avoid excessive API calls
  const debouncedLat = useDebounce(latitude, 500);
  const debouncedLng = useDebounce(longitude, 500);
  const debouncedRadius = useDebounce(radiusKm, 300);

  const fetchNearbyCheckins = useCallback(async () => {
    if (!enabled || !debouncedLat || !debouncedLng) {
      setCheckIns([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startTime = performance.now();
      const { data, error } = await supabase.rpc('get_nearby_checkins_v3', {
        user_lat: debouncedLat,
        user_lon: debouncedLng,
        radius_km: debouncedRadius
      });

      const endTime = performance.now();
      console.log(`Map fetch took ${Math.round(endTime - startTime)}ms`);

      if (error) throw error;

      setCheckIns(data || []);
    } catch (err) {
      console.error('Error fetching nearby check-ins:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch nearby check-ins';
      setError(errorMessage);
      
      // Track error in Sentry
      if (err instanceof Error) {
        analytics.trackError(err, {
          context: 'useNearbyCheckins',
          latitude: debouncedLat,
          longitude: debouncedLng,
          radius: debouncedRadius
        });
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, debouncedLat, debouncedLng, debouncedRadius]);

  useEffect(() => {
    fetchNearbyCheckins();
  }, [fetchNearbyCheckins]);

  return {
    checkIns,
    loading,
    error,
    refetch: fetchNearbyCheckins
  };
}
